import React, { useEffect, useMemo, useState, useRef } from "react";
import { useMutation } from "@apollo/client";
import { useSwipeable } from "react-swipeable";
import { GistType } from "../../services/types";
import {
  DELETE_GIST,
  PUBLISH_GIST,
  MOVE_GIST_TO_DRAFT,
} from "../../services/graphql/queriesMutations";
import { sortGistsByTime } from "../../services/utils/sortGistsByTime";
import { useGlobalContext } from "../context/AuthContext";
import Navigation from "../others/Navigation";
import Version from "./Version";

type GistProps = {
  gists: GistType[];
  onFocusModeChange?: (value: boolean) => void;
  onAfterDelete?: () => void | Promise<void>;
  targetPath?: number[] | null;
};

const CONTEXT_WINDOW_SIZE = 5;
const CONTEXT_SIDE_SIZE = 2;
const MOBILE_CONTEXT_MEDIA_QUERY = "(max-width: 768px)";

const useMediaQuery = (query: string): boolean => {
  const getMatches = () => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }

    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState<boolean>(getMatches);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQueryList = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    setMatches(mediaQueryList.matches);

    if (typeof mediaQueryList.addEventListener === "function") {
      mediaQueryList.addEventListener("change", handleChange);
      return () => mediaQueryList.removeEventListener("change", handleChange);
    }

    mediaQueryList.addListener(handleChange);
    return () => mediaQueryList.removeListener(handleChange);
  }, [query]);

  return matches;
};

type GistContextRole = "ancestor" | "selected" | "descendant";

type GistContextItem = {
  gist: GistType;
  role: GistContextRole;
  path: number[];
};

const findGistById = (nodes: GistType[], targetId: number | null): GistType | null => {
  if (!targetId) return null;

  for (const node of nodes) {
    if (node.id === targetId) return node;

    if (Array.isArray(node.gists) && node.gists.length > 0) {
      const found = findGistById(node.gists, targetId);
      if (found) return found;
    }
  }

  return null;
};

const findGistPathNodes = (nodes: GistType[], path: number[] | null | undefined): GistType[] | null => {
  if (!path || path.length === 0) return null;

  let currentLevel = nodes;
  const pathNodes: GistType[] = [];

  for (const gistId of path) {
    const match = currentLevel.find((gist) => gist.id === gistId);
    if (!match) return null;

    pathNodes.push(match);
    currentLevel = Array.isArray(match.gists) ? match.gists : [];
  }

  return pathNodes;
};

const collectDescendantContext = (
  gist: GistType,
  parentPath: number[],
  limit: number,
): GistContextItem[] => {
  if (!Array.isArray(gist.gists) || gist.gists.length === 0 || limit <= 0) {
    return [];
  }

  const descendants: GistContextItem[] = [];
  const sortedChildren = sortGistsByTime(gist.gists, "desc");

  for (const child of sortedChildren) {
    if (descendants.length >= limit) break;

    const childPath = [...parentPath, child.id];
    descendants.push({ gist: child, role: "descendant", path: childPath });

    if (descendants.length < limit) {
      descendants.push(...collectDescendantContext(child, childPath, limit - descendants.length));
    }
  }

  return descendants.slice(0, limit);
};

const buildGistContextWindow = (
  gists: GistType[],
  targetPath: number[] | null | undefined,
): GistContextItem[] | null => {
  const pathNodes = findGistPathNodes(gists, targetPath);
  if (!pathNodes || !targetPath || pathNodes.length === 0) return null;

  const selectedGist = pathNodes[pathNodes.length - 1];
  const ancestorCandidates: GistContextItem[] = pathNodes.slice(0, -1).map((gist, index) => ({
    gist,
    role: "ancestor",
    path: targetPath.slice(0, index + 1),
  }));
  const descendantCandidates = collectDescendantContext(
    selectedGist,
    targetPath,
    CONTEXT_WINDOW_SIZE - 1,
  );

  let beforeCount = Math.min(CONTEXT_SIDE_SIZE, ancestorCandidates.length);
  let afterCount = Math.min(CONTEXT_SIDE_SIZE, descendantCandidates.length);

  if (beforeCount < CONTEXT_SIDE_SIZE) {
    afterCount = Math.min(
      descendantCandidates.length,
      afterCount + (CONTEXT_SIDE_SIZE - beforeCount),
    );
  }

  if (afterCount < CONTEXT_SIDE_SIZE) {
    beforeCount = Math.min(
      ancestorCandidates.length,
      beforeCount + (CONTEXT_SIDE_SIZE - afterCount),
    );
  }

  let before = ancestorCandidates.slice(ancestorCandidates.length - beforeCount);
  const after = descendantCandidates.slice(0, afterCount);

  while (before.length + 1 + after.length < CONTEXT_WINDOW_SIZE) {
    if (after.length < descendantCandidates.length) {
      after.push(descendantCandidates[after.length]);
      continue;
    }

    const remainingBefore = ancestorCandidates.slice(0, ancestorCandidates.length - before.length);
    if (remainingBefore.length > 0) {
      before = [remainingBefore[remainingBefore.length - 1], ...before];
      continue;
    }

    break;
  }

  return [
    ...before,
    { gist: selectedGist, role: "selected", path: targetPath },
    ...after,
  ];
};

const getContextRoleLabel = (role: GistContextRole): string => {
  switch (role) {
    case "ancestor":
      return "Context above";
    case "descendant":
      return "Reply context";
    case "selected":
    default:
      return "Selected gist";
  }
};

const GistContextCard: React.FC<{
  item: GistContextItem;
  index: number;
  total: number;
  isFocusMode: boolean;
  onEnterFocusMode: () => void;
  onExitFocusMode: () => void;
  onAfterDelete?: () => void | Promise<void>;
  selectedCardRef?: React.RefObject<HTMLDivElement>;
}> = ({
  item,
  index,
  total,
  isFocusMode,
  onEnterFocusMode,
  onExitFocusMode,
  onAfterDelete,
  selectedCardRef,
}) => {
  const [showChild, setShowChild] = useState(false);
  const [versionCurrentIndex, setVersionCurrentIndex] = useState(0);
  const [editCurrentIndex, setEditCurrentIndex] = useState(0);
  const gist = item.gist;
  const hasReplies = Array.isArray(gist.gists) && gist.gists.length > 0;
  const isSelected = item.role === "selected";

  useEffect(() => {
    setShowChild(false);
    setVersionCurrentIndex(0);
    setEditCurrentIndex(0);
  }, [gist.id]);

  return (
    <div
      ref={isSelected ? selectedCardRef : undefined}
      className={[
        "gist-context-card",
        `gist-context-card-${item.role}`,
        isSelected ? "is-selected" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="gist-shell-card">
        <div className="gist-head">
          <div className="gist-meta">
            <div className="gist-context-label-row">
              <span className="gist-level-chip gist-context-chip">
                {getContextRoleLabel(item.role)}
              </span>
              <span className="gist-context-position">
                {index + 1} / {total}
              </span>
            </div>

            <div className="gist-nav">
              <button
                className={`gist-focus-toggle ${isFocusMode ? "is-active" : ""}`}
                onClick={isFocusMode ? onExitFocusMode : onEnterFocusMode}
                type="button"
              >
                {isFocusMode ? "Exit focus" : "Focus mode"}
              </button>

              {hasReplies && (
                <button
                  className={`gist-reply-toggle ${showChild ? "is-active" : ""}`}
                  onClick={() => setShowChild((current) => !current)}
                  type="button"
                >
                  {showChild ? "Hide replies" : "Show replies"}
                </button>
              )}
            </div>
          </div>

          <div className="gist-title-zone">
            <h1 className="gist-title-text">{gist.title}</h1>
          </div>
        </div>

        {gist.versions && (
          <Version
            versions={gist.versions}
            isFocusMode={isFocusMode}
            onEnterFocusMode={onEnterFocusMode}
            onExitFocusMode={onExitFocusMode}
            editCurrentIndex={editCurrentIndex}
            setEditCurrentIndex={setEditCurrentIndex}
            versionCurrentIndex={versionCurrentIndex}
            setVersionCurrentIndex={setVersionCurrentIndex}
            gistCurrentIndex={gist.id}
            gistLength={1}
            showComments={isSelected}
          />
        )}
      </div>

      <div className={`${showChild ? "flex w-full mt-4" : "hidden"}`}>
        {hasReplies && (
          <Gist
            gists={gist.gists}
            onAfterDelete={onAfterDelete}
            targetPath={null}
          />
        )}
      </div>
    </div>
  );
};

const Gist: React.FC<GistProps> = ({
  gists,
  onFocusModeChange,
  onAfterDelete,
  targetPath,
}) => {
  const targetGistId = targetPath?.[targetPath.length - 1] ?? null;
  const targetPathKey = targetPath?.join(">") ?? "";
  const contextWindow = useMemo(
    () => buildGistContextWindow(gists, targetPath),
    [gists, targetPath],
  );
  const focusedTargetGist = useMemo(
    () => (contextWindow ? null : findGistById(gists, targetGistId)),
    [contextWindow, gists, targetGistId],
  );
  const displayGists = useMemo(
    () => (focusedTargetGist ? [focusedTargetGist] : gists),
    [focusedTargetGist, gists],
  );
  const sortedGists = useMemo(() => sortGistsByTime(displayGists, "desc"), [displayGists]);
  const [showChild, setShowChild] = useState(false);
  const [dragOffset, setDragOffset] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [gistCurrentIndex, setGistCurrentIndex] = useState<number>(0);
  const [versionCurrentIndex, setVersionCurrentIndex] = useState<number>(0);
  const [editCurrentIndex, setEditCurrentIndex] = useState<number>(0);
  const [isFocusMode, setIsFocusMode] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string>("");
  const lastAppliedTargetPathRef = useRef<string | null>(null);
  const selectedContextCardRef = useRef<HTMLDivElement | null>(null);
  const readerRootRef = useRef<HTMLDivElement | null>(null);
  const isMobileContextLayout = useMediaQuery(MOBILE_CONTEXT_MEDIA_QUERY);
  const [isMobileContextExpanded, setIsMobileContextExpanded] = useState<boolean>(false);
  const { user } = useGlobalContext();
  const isAdmin = user?.userType === "admin";
  const [deleteGist, { loading: deleteLoading }] = useMutation(DELETE_GIST);
  const [publishGist, { loading: publishLoading }] = useMutation(PUBLISH_GIST);
  const [moveGistToDraft, { loading: draftLoading }] = useMutation(MOVE_GIST_TO_DRAFT);
  const selectedContextIndex = useMemo(
    () => contextWindow?.findIndex((item) => item.role === "selected") ?? -1,
    [contextWindow],
  );
  const visibleContextItems = useMemo(() => {
    if (!contextWindow) return [];

    const indexedItems = contextWindow.map((item, index) => ({ item, index }));

    if (!isMobileContextLayout || isMobileContextExpanded || contextWindow.length <= 1) {
      return indexedItems;
    }

    const selectedIndex = selectedContextIndex >= 0 ? selectedContextIndex : 0;
    return indexedItems.filter(({ index }) => index === selectedIndex);
  }, [contextWindow, isMobileContextExpanded, isMobileContextLayout, selectedContextIndex]);
  const hiddenMobileContextCount = Math.max((contextWindow?.length ?? 0) - visibleContextItems.length, 0);
  const hasHiddenMobileContext = isMobileContextLayout && hiddenMobileContextCount > 0;

  useEffect(() => {
    setIsMobileContextExpanded(false);
  }, [targetPathKey]);

  useEffect(() => {
    if (!targetPath || targetPath.length === 0) return;

    if (lastAppliedTargetPathRef.current === targetPathKey) return;

    lastAppliedTargetPathRef.current = targetPathKey;

    if (contextWindow) {
      setGistCurrentIndex(0);
      setVersionCurrentIndex(0);
      setEditCurrentIndex(0);
      setShowChild(false);
      return;
    }

    if (focusedTargetGist) {
      setGistCurrentIndex(0);
      setVersionCurrentIndex(0);
      setEditCurrentIndex(0);
      setShowChild(false);
      return;
    }

    const currentTargetId = targetPath[0];
    const targetIndex = sortedGists.findIndex((g) => g.id === currentTargetId);

    if (targetIndex !== -1) {
      setGistCurrentIndex(targetIndex);
      setVersionCurrentIndex(0);
      setEditCurrentIndex(0);

      if (targetPath.length > 1) {
        setShowChild(true);
      }
    }
  }, [targetPath, targetPathKey, sortedGists, focusedTargetGist, contextWindow]);

  useEffect(() => {
    if (!targetPath || targetPath.length === 0) return;

    const scrollTarget = contextWindow ? selectedContextCardRef.current : readerRootRef.current;
    if (!scrollTarget) return;

    const timer = window.setTimeout(() => {
      // If there's a hash, let the browser or other specific logic handle it
      if (window.location.hash) return;

      scrollTarget.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }, 120);

    return () => window.clearTimeout(timer);
  }, [contextWindow, targetPath]);

  useEffect(() => {
    if (gistCurrentIndex >= sortedGists.length && sortedGists.length > 0) {
      setGistCurrentIndex(sortedGists.length - 1);
    }
  }, [gistCurrentIndex, sortedGists.length]);

  const handleNext = () => {
    setGistCurrentIndex((prevIndex) => (prevIndex + 1) % sortedGists.length);
    setVersionCurrentIndex(0);
    setEditCurrentIndex(0);
  };

  const handlePrev = () => {
    setGistCurrentIndex((prevIndex) =>
      prevIndex === 0 ? sortedGists.length - 1 : prevIndex - 1
    );
    setVersionCurrentIndex(0);
    setEditCurrentIndex(0);
  };

  const handleIndexChange = (newIndex: number) => {
    setGistCurrentIndex(newIndex);
    setVersionCurrentIndex(0);
    setEditCurrentIndex(0);
  };

  useEffect(() => {
    onFocusModeChange?.(isFocusMode);
    if (isFocusMode) {
      setShowChild(false);
    }
  }, [isFocusMode, onFocusModeChange]);

  const handleSwipe = useSwipeable({
    onSwiping: (event) => {
      setIsDragging(true);
      setDragOffset(event.deltaX);
    },
    onSwiped: (event) => {
      setIsDragging(false);
      if (event.deltaX > 100) {
        handlePrev();
      } else if (event.deltaX < -100) {
        handleNext();
      }
      setDragOffset(0);
    },
    onTap: () => {
      setIsDragging(false);
      setDragOffset(0);
    },
    // Mouse drag is disabled so users can select/copy text without moving the carousel.
    // Touch swipes still work on mobile; desktop navigation remains available through controls.
    trackMouse: false,
  });

  if (contextWindow && contextWindow.length > 0) {
    const shouldShowMobileContextToggle = hasHiddenMobileContext || (isMobileContextLayout && contextWindow.length > 1);

    return (
      <div ref={readerRootRef} className={`gist-thread-root gist-context-root ${isFocusMode ? "is-focus-mode" : ""}`}>
        <div className="gist-context-stack" aria-label="Nearby discussion context">
          <div className="gist-context-intro">
            <span className="gist-level-chip gist-context-chip">Thread context</span>
            <span className="gist-context-intro-copy">
              {isMobileContextLayout
                ? hasHiddenMobileContext && !isMobileContextExpanded
                  ? "Showing the selected gist first. Parent and reply context is collapsed for mobile reading."
                  : "Showing the selected gist. No nearby context is hidden on this mobile view."
                : "Showing the selected gist with nearby parent and reply context."}
            </span>

            {shouldShowMobileContextToggle && (
              <button
                className="gist-context-mobile-toggle"
                type="button"
                aria-expanded={isMobileContextExpanded}
                onClick={() => setIsMobileContextExpanded((current) => !current)}
              >
                {isMobileContextExpanded
                  ? "Hide context"
                  : `Show context (${Math.max(hiddenMobileContextCount, 0)})`}
              </button>
            )}
          </div>

          {hasHiddenMobileContext && !isMobileContextExpanded && (
            <div className="gist-context-mobile-summary" role="note">
              <span className="gist-context-mobile-summary-text">
                Mobile view is focused on the selected gist. Use Show context to reveal nearby parent and reply gists.
              </span>
            </div>
          )}

          {visibleContextItems.map(({ item, index }) => (
            <GistContextCard
              key={`${item.role}-${item.gist.id}-${item.path.join(">")}`}
              item={item}
              index={index}
              total={contextWindow.length}
              isFocusMode={isFocusMode}
              onEnterFocusMode={() => setIsFocusMode(true)}
              onExitFocusMode={() => setIsFocusMode(false)}
              onAfterDelete={onAfterDelete}
              selectedCardRef={item.role === "selected" ? selectedContextCardRef : undefined}
            />
          ))}
        </div>
      </div>
    );
  }

  if (!displayGists || displayGists.length === 0) {
    return (
      <div
        className="text-center py-8"
        style={{ color: "var(--color-gray-warm)" }}
      >
        No Gists available.
      </div>
    );
  }

  const activeGist = sortedGists[gistCurrentIndex];
  const canManageActiveGist = Boolean(user && activeGist && (user.id === activeGist.userId || isAdmin));
  const isDraft = activeGist?.status === "DRAFT";

  const handlePublishActiveGist = async () => {
    if (!activeGist || !canManageActiveGist || publishLoading) return;

    setDeleteError("");
    try {
      await publishGist({ variables: { id: activeGist.id } });
      await onAfterDelete?.();
    } catch (error: any) {
      setDeleteError(error?.message || "Failed to publish gist");
    }
  };

  const handleMoveActiveGistToDraft = async () => {
    if (!activeGist || !canManageActiveGist || draftLoading) return;

    const confirmed = window.confirm(
      `Move gist "${activeGist.title}" back to Draft?

This is allowed only while the gist has no comments or reply-gists.`
    );

    if (!confirmed) return;

    setDeleteError("");
    try {
      await moveGistToDraft({ variables: { id: activeGist.id } });
      await onAfterDelete?.();
    } catch (error: any) {
      setDeleteError(error?.message || "Failed to move gist to draft");
    }
  };

  const handleDeleteActiveGist = async () => {
    if (!activeGist || !isAdmin || deleteLoading) return;

    const confirmed = window.confirm(
      `Delete gist "${activeGist.title}"?\n\nThis will also delete its versions, edits, comments, favorites, and child gists. This action cannot be undone.`
    );

    if (!confirmed) return;

    setDeleteError("");
    try {
      await deleteGist({ variables: { id: activeGist.id } });
      setShowChild(false);
      setVersionCurrentIndex(0);
      setEditCurrentIndex(0);
      setGistCurrentIndex((current) => Math.max(0, Math.min(current, sortedGists.length - 2)));
      await onAfterDelete?.();
    } catch (error: any) {
      setDeleteError(error?.message || "Failed to delete gist");
    }
  };

  const gistCarouselStyle: React.CSSProperties = {
    transform: `translateX(${dragOffset}px)`,
    transition: isDragging ? "none" : "transform 0.3s ease-in-out",
    opacity: isDragging ? 0.96 : 1,
  };

  return (
    <div ref={readerRootRef} className={`gist-thread-root ${isFocusMode ? "is-focus-mode" : ""}`}>
      <div style={gistCarouselStyle}>
        <div className="gist-shell-card">
          <div className="gist-head" {...handleSwipe}>
            <div className="gist-meta">
              <div className="gist-nav-spacer" />

              <div className="gist-nav">
                <div className="gist-level-chip">Gist</div>
                <Navigation
                  currentIndex={gistCurrentIndex}
                  totalItems={sortedGists.length}
                  onChangeIndex={handleIndexChange}
                  handlePrev={handlePrev}
                  handleNext={handleNext}
                  variant="dark"
                />
                <button
                  className={`gist-focus-toggle ${isFocusMode ? "is-active" : ""}`}
                  onClick={() => setIsFocusMode((prev) => !prev)}
                  type="button"
                >
                  {isFocusMode ? "Exit focus" : "Focus mode"}
                </button>

                {activeGist?.status && (
                  <span className={`gist-status-badge ${isDraft ? "is-draft" : "is-published"}`}>
                    {isDraft ? "Draft" : "Published"}
                  </span>
                )}

                {canManageActiveGist && activeGist?.canPublish && (
                  <button
                    className="gist-lifecycle-button"
                    onClick={handlePublishActiveGist}
                    type="button"
                    disabled={publishLoading}
                    title="Publish this draft"
                  >
                    {publishLoading ? "Publishing..." : "Publish"}
                  </button>
                )}

                {canManageActiveGist && activeGist?.canMoveToDraft && (
                  <button
                    className="gist-lifecycle-button"
                    onClick={handleMoveActiveGistToDraft}
                    type="button"
                    disabled={draftLoading}
                    title="Move this unlocked gist back to draft"
                  >
                    {draftLoading ? "Moving..." : "Move to draft"}
                  </button>
                )}

                {canManageActiveGist && !activeGist?.canEditInPlace && (activeGist?.lockReasons?.length ?? 0) > 0 && (
                  <span
                    className="gist-lock-badge"
                    title={`Direct editing locked: ${activeGist?.lockReasons?.join(", ")}`}
                  >
                    Locked
                  </span>
                )}

                {activeGist?.gists?.length > 0 && (
                  <button
                    className={`gist-reply-toggle ${showChild ? "is-active" : ""}`}
                    onClick={() => setShowChild(!showChild)}
                    type="button"
                  >
                    {showChild ? "Hide reply" : "Show reply"}
                  </button>
                )}

                {isAdmin && (
                  <button
                    className="gist-admin-delete-button"
                    onClick={handleDeleteActiveGist}
                    type="button"
                    disabled={deleteLoading}
                    title="Admin: delete this gist"
                  >
                    {deleteLoading ? "Deleting..." : "Delete gist"}
                  </button>
                )}
              </div>
            </div>

            <div className="gist-title-zone">
              <h1 className="gist-title-text">{activeGist?.title}</h1>
              {deleteError && (
                <p className="gist-admin-delete-error">{deleteError}</p>
              )}
            </div>
          </div>

          {activeGist?.versions && (
            <Version
              versions={activeGist.versions}
              isFocusMode={isFocusMode}
              onEnterFocusMode={() => setIsFocusMode(true)}
              onExitFocusMode={() => setIsFocusMode(false)}
              editCurrentIndex={editCurrentIndex}
              setEditCurrentIndex={setEditCurrentIndex}
              versionCurrentIndex={versionCurrentIndex}
              setVersionCurrentIndex={setVersionCurrentIndex}
              gistCurrentIndex={gistCurrentIndex}
              gistLength={sortedGists.length}
            />
          )}
        </div>
      </div>

      <div className={`${showChild ? "flex w-full mt-4" : "hidden"}`}>
        {activeGist?.gists && activeGist.gists.length > 0 && (
          <Gist
            gists={activeGist.gists}
            onAfterDelete={onAfterDelete}
            targetPath={targetPath && activeGist.id === targetPath[0] ? targetPath.slice(1) : null}
          />
        )}
      </div>
    </div>
  );
};

export default Gist;
